import path from 'node:path'
import * as p from '@clack/prompts'
import type { AddCommandOptions } from '@core-engine/commands/runtime.js'
import { writeConfigFile } from '@core-engine/config/serialize.js'
import { detectPackageManager } from '@core-engine/utils/package-manager.js'
import { resolveAdminNamespace } from '@next-adapter/config/namespace.js'
import { resolveConfig } from '@next-adapter/config.js'
import {
  type PostGenerateResult,
  runPostGenerate,
  scanConflictPaths
} from '@next-adapter/generators/post-generate.js'
import { syncProjectCliDependency } from '@next-adapter/init/scaffolders/dependencies.js'
import {
  formatUnknownIntegrationMessage,
  isIntegrationId
} from '@next-adapter/integration-registry.js'
import { installIntegrations } from '@next-adapter/integration-runtime.js'
import {
  formatUnknownPluginMessage,
  getPluginDefinition,
  isPluginId
} from '@next-adapter/plugin-registry.js'
import { installPlugins } from '@next-adapter/plugin-runtime.js'

function printAddNextSteps(
  installKind: 'plugin' | 'integration',
  hasSchemaChanges: boolean,
  result: PostGenerateResult,
  adminRoutePath: string
): void {
  console.log('\n  Next steps:')
  console.log(`    1. Review the installed ${installKind} files`)
  if (hasSchemaChanges && result.dbPush !== 'success') {
    console.log('    2. Run database migration: db:push')
    console.log(`    3. Start the dev server and review ${adminRoutePath}`)
    return
  }

  console.log(`    2. Start the dev server and review ${adminRoutePath}`)
}

export async function runAddCommand(items: string[], options: AddCommandOptions): Promise<void> {
  const installIntegrationsMode = Boolean(options.integration)
  const pluginIds = items.filter(isPluginId)
  const integrationIds = items.filter(isIntegrationId)

  if (!installIntegrationsMode && integrationIds.length > 0) {
    p.log.error(
      `Integration IDs require --integration. Run \`betterstart add --integration ${integrationIds.join(' ')}\`.`
    )
    process.exit(1)
  }

  if (installIntegrationsMode && pluginIds.length > 0) {
    p.log.error(`Plugin IDs cannot be installed with --integration: ${pluginIds.join(', ')}`)
    process.exit(1)
  }

  const invalidItems = installIntegrationsMode
    ? items.filter((integrationId) => !isIntegrationId(integrationId))
    : items.filter((pluginId) => !isPluginId(pluginId))
  if (invalidItems.length > 0) {
    p.log.error(
      installIntegrationsMode
        ? formatUnknownIntegrationMessage(invalidItems)
        : formatUnknownPluginMessage(invalidItems)
    )
    process.exit(1)
  }

  const cwd = options.cwd ? path.resolve(options.cwd) : process.cwd()
  const config = await resolveConfig(cwd)
  const adminRoutePath = resolveAdminNamespace(config.frameworkConfig.next.namespace).routePath
  const pm = detectPackageManager(cwd)
  const cliSyncResult = await syncProjectCliDependency(cwd, pm)

  if (cliSyncResult && !cliSyncResult.success) {
    p.log.error(cliSyncResult.error ?? 'Failed to sync betterstart-cli')
    process.exit(1)
  }

  if (installIntegrationsMode) {
    const result = await installIntegrations({
      cwd,
      config,
      pm,
      integrationIds,
      interactive: !options.yes,
      includeBiome: false
    })

    writeConfigFile(cwd, result.config)

    if (result.warnings.length > 0) {
      p.note(result.warnings.join('\n'), 'Warnings')
    }

    if (
      result.installed.length === 0 &&
      result.activated.length === 0 &&
      result.skipped.length > 0
    ) {
      p.outro(`No changes made. Already installed: ${result.skipped.join(', ')}`)
      return
    }

    const conflictPaths = scanConflictPaths(cwd)
    const postGenerateResult = await runPostGenerate(
      cwd,
      result.installed[0] ?? result.activated[0] ?? 'admin',
      {
        skipMigration: true,
        skipMigrationMessage: 'no schema changes',
        hasConflicts: conflictPaths.length > 0,
        conflictPaths,
        showNextSteps: false
      }
    )

    if (conflictPaths.length === 0) {
      printAddNextSteps('integration', false, postGenerateResult, adminRoutePath)
    }

    const messages = [
      result.installed.length > 0
        ? `Installed integration${result.installed.length === 1 ? '' : 's'}: ${result.installed.join(', ')}`
        : null,
      result.activated.length > 0
        ? `Activated integration${result.activated.length === 1 ? '' : 's'}: ${result.activated.join(', ')}`
        : null
    ].filter(Boolean)

    p.outro(messages.join('\n'))
    return
  }

  const invalidPlugins = items.filter((pluginId) => !isPluginId(pluginId))
  if (invalidPlugins.length > 0) {
    p.log.error(formatUnknownPluginMessage(invalidPlugins))
    process.exit(1)
  }

  const result = await installPlugins({
    cwd,
    config,
    pm,
    pluginIds,
    interactive: !options.yes,
    includeBiome: false
  })

  writeConfigFile(cwd, result.config)

  if (result.installed.length === 0 && result.skipped.length > 0) {
    p.outro(`No changes made. Already installed: ${result.skipped.join(', ')}`)
    return
  }

  if (result.warnings.length > 0) {
    p.note(result.warnings.join('\n'), 'Warnings')
  }

  const installedSchemaNames = result.installed.flatMap((pluginId) =>
    getPluginDefinition(pluginId).schemaFiles.map((schemaFile) => schemaFile.replace(/\.json$/, ''))
  )
  const hasSchemaChanges = installedSchemaNames.length > 0
  const conflictPaths = scanConflictPaths(cwd)
  const postGenerateResult = await runPostGenerate(cwd, installedSchemaNames[0] ?? 'admin', {
    skipMigration: !hasSchemaChanges || Boolean(options.skipMigration),
    skipMigrationMessage: hasSchemaChanges ? '--skip-migration' : 'no schema changes',
    hasConflicts: conflictPaths.length > 0,
    conflictPaths,
    showNextSteps: false
  })

  if (conflictPaths.length === 0) {
    printAddNextSteps('plugin', hasSchemaChanges, postGenerateResult, adminRoutePath)
  }

  p.outro(
    `Installed plugin${result.installed.length === 1 ? '' : 's'}: ${result.installed.join(', ')}`
  )
}
